import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Item } from '../types/Types';
import axios from 'axios';

const DEFAULT_SEARCH_LIMIT = 5;

interface ItemCacheContextType {
  // Cache management
  getItem: (id: string) => Item | null;
  addItemsToCache: (items: Item[]) => void;
  preloadItemsForRows: (rows: any[]) => Promise<void>;
  isLoading: boolean;
  isPreloading: boolean;

  // Search functionality with infinite scroll
  searchItems: (query: string, page?: number, options?: { accountingTypeCodePrefix?: string; accountingTypeGroup?: string; limit?: number; hasIngredient?: boolean; statusMode?: 'notInactive' }) => Promise<{ items: Item[]; hasMore: boolean }>;

  // Fetch single item by ID (checks cache first, then fetches)
  fetchItemById: (id: string) => Promise<Item | null>;

  // Initialize with getItemById function
  initialize: (getItemByIdFn: (id: string) => Promise<any>) => void;
}

const ItemCacheContext = createContext<ItemCacheContextType | undefined>(undefined);

export const ItemCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [itemCache, setItemCache] = useState<Map<string, Item>>(new Map());
  const [searchResults, setSearchResults] = useState<Map<string, Item[]>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [isPreloading, setIsPreloading] = useState(false);
  const [getItemByIdFn, setGetItemByIdFn] = useState<((id: string) => Promise<any>) | null>(null);

  // Use refs to access caches without causing re-renders in dependent useEffects
  const itemCacheRef = useRef<Map<string, Item>>(itemCache);
  const searchResultsRef = useRef<Map<string, Item[]>>(searchResults);

  useEffect(() => {
    itemCacheRef.current = itemCache;
  }, [itemCache]);

  useEffect(() => {
    searchResultsRef.current = searchResults;
  }, [searchResults]);

  const initialize = useCallback((getItemByIdFn: (id: string) => Promise<any>) => {
    setGetItemByIdFn(() => getItemByIdFn);
  }, []);

  // STABLE function reference - uses ref to access cache
  const getItem = useCallback((id: string): Item | null => {
    return itemCacheRef.current.get(id) || null;
  }, []); // No dependencies - stable reference

  const addItemsToCache = useCallback((items: Item[]) => {
    setItemCache(prev => {
      const newCache = new Map(prev);
      items.forEach(item => {
        if (item && item._id) {
          newCache.set(item._id, item);
        }
      });
      return newCache;
    });
  }, []);

  // STABLE function reference - uses ref to access search cache
  const searchItems = useCallback(async (query: string, page: number = 1, options?: { accountingTypeCodePrefix?: string; accountingTypeGroup?: string; limit?: number; hasIngredient?: boolean; statusMode?: 'notInactive' }): Promise<{ items: Item[]; hasMore: boolean }> => {
    const effectiveLimit = options?.limit ?? DEFAULT_SEARCH_LIMIT;
    const filterKey = (options?.accountingTypeCodePrefix ? `_acc_${options.accountingTypeCodePrefix}` : '')
      + (options?.accountingTypeGroup ? `_grp_${options.accountingTypeGroup}` : '');
    const ingredientKey = options?.hasIngredient ? '_ing' : '';
    const statusModeKey = options?.statusMode ? `_sm_${options.statusMode}` : '';
    const cacheKey = `${query.toLowerCase().trim()}${filterKey}${ingredientKey}${statusModeKey}_lim_${effectiveLimit}_page_${page}`;
    const hasMoreKey = `${cacheKey}_hasMore`;

    // Return cached result if available (use ref for stable check)
    if (searchResultsRef.current.has(cacheKey)) {
      const cachedItems = searchResultsRef.current.get(cacheKey)!;
      const cachedHasMore = searchResultsRef.current.get(hasMoreKey);
      return {
        items: cachedItems,
        hasMore: cachedHasMore ? true : cachedItems.length === effectiveLimit
      };
    }

    // Note: Removed isLoading guard - it was blocking pagination requests
    // The component should handle preventing duplicate requests

    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL ?? '';
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.get(`${apiUrl}/api/items/active`, {
        headers: { 'x-auth-token': token },
        params: {
          search: query,
          sort: 'name',
          order: 'asc',
          page,
          limit: effectiveLimit,
          accountingTypeCodePrefix: options?.accountingTypeCodePrefix,
          accountingTypeGroup: options?.accountingTypeGroup,
          hasIngredient: options?.hasIngredient ? 'true' : undefined,
          statusMode: options?.statusMode,
        },
      });

      const newItems: Item[] = response.data.items || [];
      const totalPages = response.data.totalPages || 1;
      const hasMore = page < totalPages;

      // Cache the result and hasMore flag
      setSearchResults(prev => {
        const newCache = new Map(prev);
        newCache.set(cacheKey, newItems);
        if (hasMore) {
          newCache.set(hasMoreKey, [{ hasMore: true }] as any); // Store hasMore flag
        }
        return newCache;
      });

      // Cache individual items
      setItemCache(prev => {
        const newCache = new Map(prev);
        newItems.forEach(item => {
          if (item && item._id) {
            newCache.set(item._id, item);
          }
        });
        return newCache;
      });

      return { items: newItems, hasMore };

    } catch (error) {
      console.error('Error searching items:', error);
      return { items: [], hasMore: false };
    } finally {
      setIsLoading(false);
    }
  }, []); // No dependencies - uses ref for stable reference


  const preloadItemsForRows = useCallback(async (rows: any[]) => {
    if (!rows?.length) return;

    // Extract unique item IDs that aren't cached (use ref for stable check)
    const uncachedIds = new Set<string>();
    rows.forEach(row => {
      if (row.ident?._id && !itemCacheRef.current.has(row.ident._id)) {
        uncachedIds.add(row.ident._id);
      }
    });

    if (uncachedIds.size === 0) return;

    setIsPreloading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const apiUrl = process.env.REACT_APP_API_URL ?? '';
      const itemIds = Array.from(uncachedIds);

      // Use bulk-ids endpoint for SINGLE API call instead of multiple individual calls
      const response = await fetch(`${apiUrl}/api/items/bulk-ids`, {
        method: 'POST',
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: itemIds })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.items && data.items.length > 0) {
          // Add all items to cache at once
          setItemCache(prev => {
            const newCache = new Map(prev);
            data.items.forEach((item: Item) => {
              if (item && item._id) {
                newCache.set(item._id, item);
              }
            });
            return newCache;
          });
        }
      }
    } catch (error) {
      console.error('Error preloading items:', error);
    } finally {
      setIsPreloading(false);
    }
  }, []); // No dependencies - uses ref for stable reference

  // Fetch single item by ID — checks cache first, then fetches from API and caches result
  const fetchItemById = useCallback(async (id: string): Promise<Item | null> => {
    // Check cache first (stable ref access)
    const cached = itemCacheRef.current.get(id);
    // Search/list endpoints may return a partial item without shelf-life fields.
    // RN relies on deadlineMonths to suggest finished-product expiry, so only
    // treat a cached item as complete when that field was actually provided.
    if (cached && cached.deadlineMonths !== undefined) return cached;

    try {
      const apiUrl = process.env.REACT_APP_API_URL ?? '';
      const token = localStorage.getItem('token');
      if (!token) return null;

      const response = await axios.get<Item>(`${apiUrl}/api/items/${id}`, {
        headers: { 'x-auth-token': token },
      });

      const item = response.data;
      if (item && item._id) {
        const completeItem = cached ? { ...cached, ...item } : item;
        setItemCache(prev => {
          const newCache = new Map(prev);
          newCache.set(item._id, completeItem);
          return newCache;
        });
        return completeItem;
      }
      return cached || null;
    } catch {
      return cached || null;
    }
  }, []); // stable — uses ref for cache check

  const value: ItemCacheContextType = useMemo(() => ({
    getItem,
    addItemsToCache,
    preloadItemsForRows,
    isLoading,
    isPreloading,
    searchItems,
    fetchItemById,
    initialize,
  }), [
    getItem,
    addItemsToCache,
    preloadItemsForRows,
    isLoading,
    isPreloading,
    searchItems,
    fetchItemById,
    initialize,
  ]);

  return <ItemCacheContext.Provider value={value}>{children}</ItemCacheContext.Provider>;
};

export const useItemCache = (): ItemCacheContextType => {
  const context = useContext(ItemCacheContext);
  if (!context) {
    throw new Error('useItemCache must be used within ItemCacheProvider');
  }
  return context;
};
